/*
  Makes the main product gallery behave while scrolling on touch devices.

  Two theme-level issues make a vertical swipe drag the gallery sideways:

  1. Flickity is initialised with the default `dragThreshold` of 3px, so the small
     horizontal jitter of a thumb swiping down is enough to start a drag.
  2. The theme's `flickitySmoothScrolling` helper sets
     `document.ontouchmove = (e) => e.preventDefault()` on every `dragStart`, which
     blocks page scrolling for the rest of the gesture once that drag has started.

  The companion stylesheet restores Flickity's stock `touch-action: pan-y`, which lets the
  browser lock the gesture direction. This script raises the threshold and releases the
  document-wide scroll lock. Both only affect touch input.

  Scoped to the product template gallery: the featured-product section and the quick-view
  popup keep the theme's original behaviour.
*/
(() => {
  const SLIDER_SELECTOR =
    "[data-section-type='product-template'] product-info[id^='MainProduct'] [data-product-single-media-slider]";
  const DRAG_THRESHOLD = 10;

  // Physique du ressort de Flickity : accélération = (cible − x) × attraction, puis
  // vélocité amortie par la friction à chaque frame.
  //
  // Balayage mesuré sur la page produit réelle (durée jusqu'à ±0,35 px de la cible,
  // dépassement maximal au-delà de la cible) :
  //
  //   attraction / friction   stabilisation   dépassement
  //   0.025 / 0.28 (défaut)      1566 ms          0 px
  //   0.05  / 0.38 (précédent)   1066 ms          0 px
  //   0.14  / 0.62                733 ms          0 px
  //   0.18  / 0.68                633 ms          0 px   <- retenu
  //   0.22  / 0.72                533 ms          0 px
  //   0.26  / 0.74                400 ms          0 px
  //   0.50  / 0.90                263 ms          0 px
  //   0.60  / 0.90                267 ms          2 px
  //
  // Deux crans plus rapides ont été essayés et écartés au ressenti : 0.50 / 0.90 pose la photo
  // avant que l'œil ait suivi, 0.26 / 0.74 reste sec. 0.18 / 0.68 laisse voir le déplacement
  // (~630 ms) tout en restant nettement sous les 1066 ms du réglage d'origine.
  //
  // Plafond à ne pas franchir : le couple doit rester sans dépassement. Au-delà, la bande
  // repasse la cible et laisse apparaître un bandeau de la photo suivante en fin de
  // glissement — le défaut corrigé plus tôt, à ne pas réintroduire.
  //
  // La friction sert aussi à projeter la position de repos au relâchement
  // (`x + vélocité / friction`) : plus elle est haute, plus le lancer est court et
  // prévisible — un coup de pouce avance d'une photo au lieu d'en sauter deux ou trois.
  const SELECTED_ATTRACTION = 0.18;
  const FRICTION = 0.68;

  const isMobile = () => window.matchMedia('(max-width: 767px)').matches;

  // Connexion lente ou mode économie de données : on ne précharge que la photo immédiatement
  // voisine, pas les deux de chaque côté.
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const isSlowConnection = () =>
    Boolean(conn && (conn.saveData || /^(slow-2g|2g|3g)$/.test(conn.effectiveType || '')));

  /*
    Les 18 photos de la galerie sont en `loading="lazy"`. Dans un carrousel horizontal, le
    navigateur ne déclenche le téléchargement qu'une fois la photo entrée dans la fenêtre,
    c'est-à-dire *pendant* le glissement : sur une connexion mobile réelle, on voit donc le
    shimmer gris à la place de l'image pendant plusieurs centaines de millisecondes. Le
    glissement lui-même est fluide, mais le résultat perçu ne l'est pas.

    On repasse en `eager` les voisines de la photo sélectionnée, en `fetchpriority="low"` pour
    ne pas concurrencer le LCP de la page. Chaque photo pèse ~34 Ko en 848w : précharger deux
    voisines coûte 68 Ko, très en dessous du poids de la page. Le reste des slides reste lazy.
  */
  const preloadNeighbours = (flkty) => {
    const span = isSlowConnection() ? 1 : 2;
    const index = flkty.selectedIndex;

    for (let offset = -span; offset <= span; offset += 1) {
      const cell = flkty.cells[index + offset];
      if (!cell) continue;

      cell.element.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.decoding = 'async';
        img.fetchPriority = 'low';
        img.loading = 'eager';
      });
    }
  };

  /*
    Flickity déplace `.flickity-slider` en modifiant `transform` à chaque frame depuis le JS.
    Sans promotion en couche de composition, chaque frame repeint la bande visible — les 18
    slides plein écran. `will-change: transform` (posé par la classe, dans la feuille de style
    qui accompagne ce fichier) fait passer le déplacement au compositeur.

    Posé au début du geste et retiré à l'arrêt : un `will-change` permanent garderait la couche
    et sa mémoire texture allouées en continu, ce qui pénalise les téléphones d'entrée de gamme.
  */
  const bindDragLayer = (flkty, slider) => {
    let releaseTimer = null;

    const release = () => {
      clearTimeout(releaseTimer);
      releaseTimer = null;
      slider.classList.remove('is-drag-active');
    };

    flkty.on('dragStart', () => {
      clearTimeout(releaseTimer);
      slider.classList.add('is-drag-active');
    });

    // `settle` est l'événement de fin d'animation. Le filet de sécurité couvre le cas où
    // l'instance est remplacée avant qu'il ne se déclenche.
    flkty.on('settle', release);
    flkty.on('dragEnd', () => {
      clearTimeout(releaseTimer);
      releaseTimer = setTimeout(release, 800);
    });
  };

  const patchSlider = () => {
    const slider = document.querySelector(SLIDER_SELECTOR);
    if (!slider) return;

    const flkty = window.theme?.Flickity?.data(slider);
    if (!flkty || flkty.touchScrollPatched) return;

    flkty.touchScrollPatched = true;
    flkty.options.dragThreshold = DRAG_THRESHOLD;

    // Pas de défilement infini : la galerie s'arrête à la première et à la dernière photo.
    // Le bouclage obligeait la bande de vignettes à traverser toute sa longueur au passage de
    // la dernière à la première, ce qui se lisait comme un bug sur mobile.
    //
    // L'option est lue au moment du `select`, donc la changer après l'initialisation suffit
    // pour le comportement. Les flèches, elles, ont mémorisé leur état au démarrage : sans
    // ce rappel de `update()`, la flèche gauche resterait active sur la première photo. Une
    // fois à jour, Flickity pose l'attribut `disabled` aux extrémités, et la feuille de style
    // qui accompagne ce fichier masque la flèche concernée.
    flkty.options.wrapAround = false;
    if (flkty.prevButton) flkty.prevButton.update();
    if (flkty.nextButton) flkty.nextButton.update();

    // Réglages de mouvement : appliqués à toutes les largeurs. Le slider est le même au
    // doigt et à la souris, et les valeurs par défaut du thème sont lentes des deux côtés.
    flkty.options.selectedAttraction = SELECTED_ATTRACTION;
    flkty.options.friction = FRICTION;

    bindDragLayer(flkty, slider);

    if (isMobile()) {
      preloadNeighbours(flkty);
      flkty.on('select', () => preloadNeighbours(flkty));
      // Le geste commence avant le `select` : on demande les deux voisines dès le contact du
      // doigt, ce qui laisse la durée du glissement au téléchargement.
      flkty.on('dragStart', () => preloadNeighbours(flkty));
    }

    // Added after the theme's own dragStart listener, so it runs second and undoes the lock
    flkty.on('dragStart', () => {
      document.ontouchmove = null;
    });
  };

  // Flickity is created synchronously during the product-info upgrade, so wait a frame.
  // The slider is destroyed and rebuilt on width changes and on product swap, hence the
  // listeners: each new instance needs patching again.
  const schedulePatch = () => requestAnimationFrame(patchSlider);

  schedulePatch();
  document.addEventListener('theme:product-info:loaded', schedulePatch);
  document.addEventListener('theme:resize:width', schedulePatch);
})();
